import {
  ActivityLogActorType,
  ActivityLogActorTypeLabels,
  ActivityLogDocumentType,
  ActivityLogPropertyChangeType,
} from '../constants/activityLog'
import type {
  ActivityLog,
  ActivityLogAdmissionGroupContext,
  ActivityLogChange,
} from '../activityLog'

export type ResolvedActivityLogActor = {
  actorType: ActivityLogActorType
  actorName: string
  actorEmail?: string
  secondaryLabel: string
}

const GENERIC_ACTOR_NAMES = new Set([
  'Unknown',
  'Customer',
  'Guest',
  'Promoter',
])

function isGenericActorName(name: string | null | undefined): boolean {
  if (!name?.trim()) return true
  return GENERIC_ACTOR_NAMES.has(name.trim())
}

function normalizeString(value: unknown): string | undefined {
  if (value == null || value === '') return undefined
  return String(value)
}

function extractGuestNameFromChanges(
  changes: ActivityLogChange[] | undefined
): string | undefined {
  if (!changes?.length) return undefined

  for (const change of changes) {
    if (change.keyPath !== 'name') continue

    const value =
      change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY
        ? change.oldValue
        : (change.newValue ?? change.oldValue)

    const name = normalizeString(value)
    if (name) return name
  }

  return undefined
}

function extractGuestEmailFromChanges(
  changes: ActivityLogChange[] | undefined
): string | undefined {
  if (!changes?.length) return undefined

  for (const change of changes) {
    if (change.keyPath !== 'email') continue

    const value =
      change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY
        ? change.oldValue
        : (change.newValue ?? change.oldValue)

    const email = normalizeString(value)
    if (email) return email
  }

  return undefined
}

function resolveReservationGuestName(
  activityLog: Pick<ActivityLog, 'admissionGroupContext' | 'changes'>
): string | undefined {
  return (
    activityLog.admissionGroupContext?.name ??
    extractGuestNameFromChanges(activityLog.changes)
  )
}

function resolveReservationGuestEmail(
  activityLog: Pick<ActivityLog, 'admissionGroupContext' | 'changes'>
): string | undefined {
  return (
    activityLog.admissionGroupContext?.email ??
    extractGuestEmailFromChanges(activityLog.changes)
  )
}

function resolveLegacyActorType(
  activityLog: Pick<
    ActivityLog,
    | 'managementUser'
    | 'managementUserName'
    | 'documentType'
    | 'admissionGroupContext'
    | 'changes'
  >
): ActivityLogActorType {
  if (activityLog.managementUser || activityLog.managementUserName) {
    return ActivityLogActorType.STAFF
  }

  if (
    activityLog.documentType === ActivityLogDocumentType.ADMISSION_GROUP &&
    resolveReservationGuestName(activityLog)
  ) {
    return ActivityLogActorType.CUSTOMER
  }

  return ActivityLogActorType.CUSTOMER
}

function resolveLegacyActorName(
  activityLog: Pick<
    ActivityLog,
    | 'managementUserName'
    | 'managementUserEmail'
    | 'admissionGroupContext'
    | 'actorName'
    | 'changes'
  >,
  actorType: ActivityLogActorType
): string {
  const reservationGuestName = resolveReservationGuestName(activityLog)

  if (activityLog.actorName && !isGenericActorName(activityLog.actorName)) {
    return activityLog.actorName
  }

  if (actorType === ActivityLogActorType.STAFF) {
    return (
      activityLog.managementUserName ||
      activityLog.managementUserEmail ||
      'Unknown'
    )
  }

  if (actorType === ActivityLogActorType.PROMOTER) {
    return (
      activityLog.admissionGroupContext?.attributionName ||
      reservationGuestName ||
      activityLog.actorName ||
      'Promoter'
    )
  }

  return reservationGuestName || activityLog.actorName || 'Customer'
}

function resolveSecondaryLabel(
  actorType: ActivityLogActorType,
  actorEmail?: string | null
): string {
  if (
    actorType === ActivityLogActorType.STAFF ||
    actorType === ActivityLogActorType.ADMIN
  ) {
    return actorEmail || ''
  }

  return ActivityLogActorTypeLabels[actorType]
}

export function resolveActivityLogActor(
  activityLog: Pick<
    ActivityLog,
    | 'actorType'
    | 'actorName'
    | 'actorEmail'
    | 'managementUser'
    | 'managementUserName'
    | 'managementUserEmail'
    | 'documentType'
    | 'admissionGroupContext'
    | 'changes'
  >
): ResolvedActivityLogActor {
  const hasManagementIdentity = Boolean(
    activityLog.managementUser || activityLog.managementUserName
  )
  const isStaffLikeActorType =
    activityLog.actorType === ActivityLogActorType.STAFF ||
    activityLog.actorType === ActivityLogActorType.ADMIN
  const actorType = hasManagementIdentity
    ? isStaffLikeActorType
      ? activityLog.actorType!
      : ActivityLogActorType.STAFF
    : (activityLog.actorType ?? resolveLegacyActorType(activityLog))
  const actorName = hasManagementIdentity
    ? activityLog.managementUserName ||
      activityLog.managementUserEmail ||
      'Unknown'
    : resolveLegacyActorName(activityLog, actorType)
  const actorEmail = hasManagementIdentity
    ? (activityLog.managementUserEmail ?? undefined)
    : (activityLog.actorEmail ??
      (actorType === ActivityLogActorType.STAFF
        ? (activityLog.managementUserEmail ?? undefined)
        : resolveReservationGuestEmail(activityLog)))

  return {
    actorType,
    actorName,
    actorEmail: actorEmail ?? undefined,
    secondaryLabel: resolveSecondaryLabel(actorType, actorEmail),
  }
}

export function admissionGroupContextGuestName(
  context?: ActivityLogAdmissionGroupContext | null
): string | undefined {
  return context?.name ?? undefined
}
